"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Timer } from "@/components/timer"
import { TimesheetList } from "@/components/timesheet-list"
import { StatsView } from "@/components/stats-view"
import { TaskSelector } from "@/components/task-selector"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  fetchUserTasks,
  fetchUserTimesheets,
  fetchNotionDataFromWP,
  refreshNotionDataAndClearCache,
} from "@/lib/notion-client"
import type { Task, TimesheetEntry } from "@/lib/types"
import { Clock, FileText, BarChart3, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { useTimer } from "@/lib/timer-context"
import { useToast } from "@/hooks/use-toast"
import { loadDraftEntries, saveDraftEntries, clearDraftEntries } from "@/lib/draft-entries"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function TimeTrackerPage() {
  const { user } = useWordPressAuth()
  const { submitAllData, currentEntry } = useTimer()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { tab, taskId } = useParams<{ tab?: string; taskId?: string }>()

  const [tasks, setTasks] = useState<Task[]>([])
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([])
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [syncLoading, setSyncLoading] = useState(false)
  const [lastSync, setLastSync] = useState<string | null>(null)
  const [submitResult, setSubmitResult] = useState<any | null>(null)
  const [showSubmitDialog, setShowSubmitDialog] = useState(false)
  const [showLeaveWarning, setShowLeaveWarning] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [taskStatusUpdates, setTaskStatusUpdates] = useState<Record<string, Task["status"]>>({})
  const [activeTab, setActiveTab] = useState<string>("timer")

  const hasUnsavedData = !!currentEntry || timesheets.some((entry) => entry.status === "Draft")

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user])

  // Handle URL parameters
  useEffect(() => {
    const validTabs = ["timer", "timesheet", "stats"]
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab)
    } else if (tab && !validTabs.includes(tab)) {
      // Redirect to default tab if invalid
      navigate("/tracker/timer", { replace: true })
    } else if (!tab) {
      // Default to timer tab if no tab specified
      setActiveTab("timer")
    }
  }, [tab, navigate])

  // Handle task URL parameter
  useEffect(() => {
    if (taskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskId)
      if (task) {
        setSelectedTask(task)
      }
    } else if (!taskId) {
      // Clear selected task if no taskId in URL
      setSelectedTask(null)
    }
  }, [taskId, tasks])

  useEffect(() => {
    // track unsaved data state
  }, [hasUnsavedData, currentEntry, timesheets])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) {
        e.preventDefault()
        e.returnValue = ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [hasUnsavedData])

  async function loadData() {
    setIsLoading(true)
    setSyncLoading(true)
    
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    
    try {
      if (isDev) console.log('[loadData] Starting fetch from WordPress...')
      const resp = await fetchNotionDataFromWP()
      if (isDev) console.log('[loadData] WordPress response:', resp)
      
      if (resp && resp.status === 'ok' && resp.data) {
        if (isDev) console.log('[loadData] Success! Loading real data')
        if (resp.data.tasks) setTasks(resp.data.tasks as any)
        if (resp.data.timesheets) {
          // Merge server timesheets with local draft entries
          const serverTimesheets = resp.data.timesheets as any
          const draftEntries = loadDraftEntries()
          if (isDev) {
            console.log('[loadData] Server timesheets:', serverTimesheets.length)
            console.log('[loadData] Draft entries:', draftEntries.length)
          }
          // Only merge submitted entries from server with draft entries from localStorage
          const submittedFromServer = serverTimesheets.filter((t: any) => t.status === 'Submitted')
          const merged = [...submittedFromServer, ...draftEntries]
          if (isDev) console.log('[loadData] Merged total:', merged.length)
          setTimesheets(merged)
        }
        if (resp.generatedAt) setLastSync(resp.generatedAt)
        return
      }
      
      if (resp && resp.status === 'error') {
        console.error('[loadData] Error response from WordPress:', resp)
        const msg = resp.meta?.message || 'Unknown error from data sync'
        toast({ title: 'Sync error', description: msg })
        return
      }
      
      if (isDev) console.warn('[loadData] Unexpected response, falling back to dummy data:', resp)
      const userId = user?.id.toString() || "user-1"
      const [tasksData, timesheetsData] = await Promise.all([fetchUserTasks(userId), fetchUserTimesheets(userId)])
      setTasks(tasksData)
      // Convert NotionTimesheet to TimesheetEntry format
      const convertedTimesheets: TimesheetEntry[] = timesheetsData.map(ts => ({
        id: ts.id,
        taskId: ts.taskId,
        startTime: ts.startTime.toISOString(),
        endTime: ts.endTime?.toISOString(),
        status: ts.status,
        userId: ts.userId,
        manualMinutes: ts.manualMinutes,
        isSubmitted: ts.isSubmitted,
      }))
      setTimesheets(convertedTimesheets)
    } catch (error) {
      console.error('[loadData] Fetch failed with error:', error)
      const userId = user?.id.toString() || "user-1"
      const [tasksData, timesheetsData] = await Promise.all([fetchUserTasks(userId), fetchUserTimesheets(userId)])
      setTasks(tasksData)
      // Convert NotionTimesheet to TimesheetEntry format
      const convertedTimesheets: TimesheetEntry[] = timesheetsData.map(ts => ({
        id: ts.id,
        taskId: ts.taskId,
        startTime: ts.startTime.toISOString(),
        endTime: ts.endTime?.toISOString(),
        status: ts.status,
        userId: ts.userId,
        manualMinutes: ts.manualMinutes,
        isSubmitted: ts.isSubmitted,
      }))
      setTimesheets(convertedTimesheets)
    } finally {
      setSyncLoading(false)
      setIsLoading(false)
    }
  }

  async function handleRefresh() {
    setIsLoading(true)
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    try {
      const resp = await refreshNotionDataAndClearCache()
      if (resp && resp.status === 'ok' && resp.data) {
        if (resp.data.tasks) setTasks(resp.data.tasks as any)
        if (resp.data.timesheets) setTimesheets(resp.data.timesheets as any)
        if (resp.generatedAt) setLastSync(resp.generatedAt)
      } else {
        // optionally keep existing data on error
        if (isDev) console.warn('Refresh returned no data', resp)
      }
    } catch (err) {
      console.error('Failed to refresh notion data', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab)
    navigate(`/tracker/${newTab}`, { replace: true })
  }

  const handleTaskSelect = (task: Task | null) => {
    setSelectedTask(task)
    if (task) {
      navigate(`/tracker/${activeTab}/task/${task.id}`)
    } else {
      navigate(`/tracker/${activeTab}`)
    }
  }

  const handleTimesheetUpdate = (updatedEntry: TimesheetEntry) => {
    setTimesheets((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.id === updatedEntry.id)
      let updated
      if (existingIndex >= 0) {
        updated = prev.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry))
      } else {
  // added new entry
        updated = [...prev, updatedEntry]
      }
      // Save draft entries to localStorage
      const drafts = updated.filter(e => e.status === "Draft")
      saveDraftEntries(drafts)
      return updated
    })
  }

  const handleTimesheetDelete = (entryId: string) => {
    setTimesheets((prev) => {
      const updated = prev.filter((entry) => entry.id !== entryId)
      // Update localStorage
      const drafts = updated.filter(e => e.status === "Draft")
      saveDraftEntries(drafts)
      return updated
    })
  // entry deleted
  }

  const handleEntryComplete = (entry: TimesheetEntry) => {
    setTimesheets((prev) => {
      const updated = [...prev, entry]
      // Save draft entries to localStorage
      const drafts = updated.filter(e => e.status === "Draft")
      saveDraftEntries(drafts)
      return updated
    })
  // timer entry added
  }

  const handleSubmit = async () => {
    const draftEntries = timesheets.filter((entry) => entry.status === "Draft")
    const allDraftEntries = currentEntry ? [...draftEntries, currentEntry] : draftEntries

  // submitting draft entries
    setShowSubmitDialog(false)
    setIsSubmitting(true)

    if (Object.keys(taskStatusUpdates).length > 0) {
      setTasks((prev) =>
        prev.map((task) => (taskStatusUpdates[task.id] ? { ...task, status: taskStatusUpdates[task.id] } : task)),
      )
    }

    // Convert taskStatusUpdates object to array format
    const taskStatusUpdatesArray = Object.keys(taskStatusUpdates).length > 0
      ? Object.entries(taskStatusUpdates).map(([taskID, status]) => ({ taskID, status }))
      : undefined

    try {
      const resp = await submitAllData(allDraftEntries, taskStatusUpdatesArray)
      setSubmitResult(resp || { status: 'ok' })
      // If server returned ok, clear draft entries from localStorage
      if (resp && resp.status === 'ok') {
        clearDraftEntries()
      }
      // If server returned ok, the timer-context already updated local state
    } catch (err) {
      setSubmitResult({ status: 'error', message: String(err) })
    } finally {
      setIsSubmitting(false)
      setTaskStatusUpdates({})
    }
  }

  // Skeleton Loading Components
  const TimerSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6"></div>
      <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
      <div className="flex gap-2">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
      </div>
    </div>
  )

  const TaskSelectorSkeleton = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
        <div className="flex gap-2">
          <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4 animate-pulse">
          <div className="flex items-start justify-between mb-2">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
        </div>
      ))}
    </div>
  )

  const TimesheetSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
      <div className="p-6 border-b dark:border-gray-700">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 animate-pulse"></div>
      </div>
      <div className="divide-y dark:divide-gray-700">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-4 animate-pulse">
            <div className="flex items-center justify-between mb-3">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const StatsSkeleton = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
          </div>
        ))}
      </div>
      
      {/* Chart Skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-6"></div>
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs value={activeTab} className="space-y-6">
            {/* Tab List with Actions - Still visible during loading */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
              <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <TabsList className="grid grid-cols-3 h-12 bg-gray-100 dark:bg-gray-700">
                <TabsTrigger 
                  value="timer" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                  disabled
                >
                  <Clock className="h-5 w-5" />
                  <span className="text-base">Timer</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="timesheet" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                  disabled
                >
                  <FileText className="h-5 w-5" />
                  <span className="text-base">Timesheet</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="stats" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                  disabled
                >
                  <BarChart3 className="h-5 w-5" />
                  <span className="text-base">Stats</span>
                </TabsTrigger>
              </TabsList>                <div className="flex items-center gap-3 mr-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500 hidden lg:block">
                    Loading...
                  </p>

                  <Button variant="outline" size="sm" disabled>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                  </Button>

                  <Button size="sm" disabled className="gap-2">
                    <Send className="h-4 w-4" />
                    <span className="hidden sm:inline">Submit</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Skeleton Content */}
            <TabsContent value="timer" className="space-y-6">
              <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
                <div className="lg:sticky lg:top-24">
                  <TimerSkeleton />
                </div>
                <div>
                  <TaskSelectorSkeleton />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="timesheet">
              <TimesheetSkeleton />
            </TabsContent>

            <TabsContent value="tasks">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="stats">
              <StatsSkeleton />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {/* Improved Tab List with Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <TabsList className="grid grid-cols-3 h-12 bg-gray-100 dark:bg-gray-700">
                <TabsTrigger 
                  value="timer" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                >
                  <Clock className="h-5 w-5" />
                  <span className="text-base">Timer</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="timesheet" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                >
                  <FileText className="h-5 w-5" />
                  <span className="text-base">Timesheet</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="stats" 
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-gray-900 font-medium px-6"
                >
                  <BarChart3 className="h-5 w-5" />
                  <span className="text-base">Stats</span>
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-3 mr-2">
                {lastSync && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block">
                    Last sync: {new Date(lastSync).toLocaleString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                    })}
                  </p>
                )}

                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={syncLoading}>
                  {syncLoading ? 'Syncing...' : 'Refresh'}
                </Button>

                <Button
                  onClick={() => setShowSubmitDialog(true)}
                  disabled={!hasUnsavedData}
                  size="sm"
                  className={`gap-2 ${hasUnsavedData ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                >
                  <Send className="h-4 w-4" />
                  <span className="hidden sm:inline">Submit</span>
                  {hasUnsavedData && ` (${timesheets.filter((e) => e.status === "Draft").length + (currentEntry ? 1 : 0)})`}
                </Button>
              </div>
            </div>
          </div>

          <TabsContent value="timer" className="space-y-6">
            <div className="grid lg:grid-cols-[400px_1fr] gap-6 items-start">
              <div className="lg:sticky lg:top-24">
                <Timer selectedTask={selectedTask} onEntryComplete={handleEntryComplete} />
              </div>
              <div>
                <TaskSelector tasks={tasks} selectedTask={selectedTask} onSelectTask={handleTaskSelect} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="timesheet">
            <TimesheetList
              timesheets={timesheets}
              tasks={tasks}
              onUpdate={handleTimesheetUpdate}
              onDelete={handleTimesheetDelete}
            />
          </TabsContent>

          <TabsContent value="stats">
            <StatsView timesheets={timesheets} tasks={tasks} />
          </TabsContent>
        </Tabs>
      </main>

      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Time Entries</AlertDialogTitle>
            <AlertDialogDescription>
              This will submit {timesheets.filter((e) => e.status === "Draft").length + (currentEntry ? 1 : 0)} time{" "}
              {timesheets.filter((e) => e.status === "Draft").length + (currentEntry ? 1 : 0) === 1
                ? "entry"
                : "entries"}{" "}
              to Notion. Once submitted, entries cannot be edited.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm font-medium">Update Task Status (Optional)</p>
            {Array.from(
              new Set(
                [...timesheets.filter((e) => e.status === "Draft"), ...(currentEntry ? [currentEntry] : [])].map(
                  (e) => e.taskId,
                ),
              ),
            ).map((taskId) => {
              const task = tasks.find((t) => t.id === taskId)
              if (!task) return null

              return (
                <div key={taskId} className="flex items-center gap-4 p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{task.title}</p>
                    <p className="text-xs text-muted-foreground">Current: {task.status}</p>
                  </div>
                  <div className="w-48">
                    <Select
                      value={taskStatusUpdates[taskId] || task.status}
                      onValueChange={(value) =>
                        setTaskStatusUpdates((prev) => ({ ...prev, [taskId]: value as Task["status"] }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Backlog">Backlog</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Quality Check">Quality Check</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )
            })}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTaskStatusUpdates({})}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit}>Submit All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLeaveWarning} onOpenChange={setShowLeaveWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsubmitted time entries. If you leave now, your data will be lost. Are you sure you want to
              continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Stay on Page</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/login")}>Leave Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!submitResult} onOpenChange={() => setSubmitResult(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{submitResult?.status === 'ok' ? 'Submission successful' : 'Submission result'}</AlertDialogTitle>
            <AlertDialogDescription>
              {submitResult?.status === 'ok'
                ? `Processed ${submitResult?.processed ?? 'N/A'} entries.`
                : submitResult?.message || String(submitResult)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSubmitResult(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submitting Overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-sm mx-4 text-center">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
                <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-4 border-primary border-r-transparent animate-spin"></div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Submitting Timesheets
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Please wait while we process your time entries...
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
